import { JobsiteUpdateInput } from 'apollo/generated/client-operations';
import {
  FormInputTypes,
  getUpdateInputValueFunction,
  GridColSpan,
  TypedFormInputs,
} from 'components/form';
import { EditJobsiteConfigurationFormData } from 'containers/jobsiteConfiguration/types';
import { DeepMap } from 'react-hook-form';
import { toFancySelectOptions } from 'utils/constants';
import { Jobsite } from './types';

export const getCustomFieldsSectionInputs = (
  jobsite: Jobsite,
): TypedFormInputs<EditJobsiteConfigurationFormData['customFields']> => {
  const {
    activityCodeOptions,
    classificationGroupOptions,
    tradeOptions,
    titleOptions,
    unionAffiliationOptions,
  } = jobsite ?? {};

  return {
    activityCodeOptions: {
      element: FormInputTypes.CreatableSelect,
      label: 'Activity Code Options',
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
      elementProps: {
        isMulti: true,
        clearToNull: true,
        options: toFancySelectOptions(activityCodeOptions ?? []),
      },
    },
    classificationGroupOptions: {
      element: FormInputTypes.CreatableSelect,
      label: 'Classification Group Options',
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
      elementProps: {
        isMulti: true,
        clearToNull: true,
        options: toFancySelectOptions(classificationGroupOptions ?? []),
      },
    },
    tradeOptions: {
      element: FormInputTypes.CreatableSelect,
      label: 'Trade Options',
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
      elementProps: {
        isMulti: true,
        clearToNull: true,
        options: toFancySelectOptions(tradeOptions ?? []),
      },
    },
    titleOptions: {
      element: FormInputTypes.CreatableSelect,
      label: 'Title Options',
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
      elementProps: {
        isMulti: true,
        clearToNull: true,
        options: toFancySelectOptions(titleOptions ?? []),
      },
    },
    unionAffiliationOptions: {
      element: FormInputTypes.CreatableSelect,
      label: 'Union Affiliation Options',
      layout: [GridColSpan.SpanFull, GridColSpan.SmSpan6],
      elementProps: {
        isMulti: true,
        clearToNull: true,
        options: toFancySelectOptions(unionAffiliationOptions ?? []),
      },
    },
  };
};

export const getCustomFieldsDefaultValues = (
  jobsite: Jobsite,
): EditJobsiteConfigurationFormData['customFields'] => {
  const {
    activityCodeOptions,
    classificationGroupOptions,
    tradeOptions,
    titleOptions,
    unionAffiliationOptions,
  } = jobsite ?? {};
  return {
    activityCodeOptions: toFancySelectOptions(activityCodeOptions ?? []),
    classificationGroupOptions: toFancySelectOptions(
      classificationGroupOptions ?? [],
    ),
    tradeOptions: toFancySelectOptions(tradeOptions ?? []),
    titleOptions: toFancySelectOptions(titleOptions ?? []),
    unionAffiliationOptions: toFancySelectOptions(
      unionAffiliationOptions ?? [],
    ),
  };
};

type CustomFieldsSectionUpdateInput = Required<
  Pick<
    JobsiteUpdateInput,
    | 'activityCodeOptions'
    | 'classificationGroupOptions'
    | 'titleOptions'
    | 'tradeOptions'
    | 'unionAffiliationOptions'
  >
>;

export const getCustomFieldsSectionUpdateInput = (
  customFields: EditJobsiteConfigurationFormData['customFields'],
  dirtyFields: DeepMap<EditJobsiteConfigurationFormData['customFields'], true>,
): CustomFieldsSectionUpdateInput => {
  const getUpdateInputValue = getUpdateInputValueFunction(
    customFields,
    dirtyFields,
  );

  return {
    activityCodeOptions: getUpdateInputValue('activityCodeOptions'),
    classificationGroupOptions: getUpdateInputValue(
      'classificationGroupOptions',
    ),
    titleOptions: getUpdateInputValue('titleOptions'),
    tradeOptions: getUpdateInputValue('tradeOptions'),
    unionAffiliationOptions: getUpdateInputValue('unionAffiliationOptions'),
  };
};
